<template>
  <v-container class="about-container">
    <!-- Hero -->
    <div class="hero text-center mb-8">
      <v-img :src="logo" alt="logo" width="96" height="96" class="mx-auto mb-3 hero-logo"></v-img>
      <h1 class="text-h3 font-weight-bold">{{ websiteName }}</h1>
      <p class="text-subtitle-1 text-medium-emphasis mt-1">
        Collaborative transcription for encrypted manuscripts
      </p>
    </div>

    <!-- Description -->
    <v-card class="pa-6 mb-6" elevation="2" rounded="lg">
      <h2 class="text-h6 mb-3 d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-information-outline</v-icon>
        What is {{ websiteName }}
      </h2>
      <p class="text-body-1 mb-4">
        {{ websiteName }} is a collaborative platform for transcribing and studying encrypted
        historical manuscripts. Project leaders organize documents and pages, assign work to
        transcribers, and track progress from first pass to validation.
      </p>
      <v-list density="compact" class="feature-list bg-transparent">
        <v-list-item v-for="feature in features" :key="feature.text" class="px-0">
          <template v-slot:prepend>
            <v-icon color="primary" size="18">mdi-check-circle</v-icon>
          </template>
          <v-list-item-title class="text-body-2">{{ feature.text }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Integrations -->
    <v-card class="pa-6 mb-6" elevation="2" rounded="lg">
      <h2 class="text-h6 mb-4 d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-puzzle-outline</v-icon>
        Integrations
      </h2>
      <v-row>
        <v-col v-for="item in about.integrations" :key="item.name" cols="12" sm="6">
          <a :href="item.link" target="_blank" rel="noopener" class="integration-card d-flex align-center pa-3">
            <v-avatar size="40" class="me-3" rounded="lg">
              <img v-if="item.logo" :src="integrationLogo(item.logo)" :alt="item.name" class="integration-logo" />
              <v-icon v-else size="24" color="primary">{{ item.icon }}</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="text-caption text-medium-emphasis">{{ item.detail }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-icon size="18" color="grey">mdi-open-in-new</v-icon>
          </a>
        </v-col>
      </v-row>
    </v-card>

    <!-- Technologies -->
    <v-card class="pa-6 mb-6" elevation="2" rounded="lg">
      <h2 class="text-h6 mb-4 d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-toolbox-outline</v-icon>
        Built with
      </h2>
      <div class="tech-grid">
        <v-chip
          v-for="tech in about.technologies"
          :key="tech.name"
          variant="tonal"
          color="primary"
          class="ma-1"
        >
          <v-icon start size="16">{{ tech.icon }}</v-icon>
          {{ tech.name }}
          <span class="text-caption text-medium-emphasis ms-1">({{ tech.detail }})</span>
        </v-chip>
      </div>
    </v-card>

    <!-- Original developer -->
    <v-card class="pa-6 mb-6" elevation="2" rounded="lg">
      <h2 class="text-h6 mb-4 d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-account-star-outline</v-icon>
        Originally developed by
      </h2>
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <component
            :is="about.originalDeveloper.link ? 'a' : 'div'"
            :href="about.originalDeveloper.link || undefined"
            :target="about.originalDeveloper.link ? '_blank' : undefined"
            rel="noopener"
            class="team-card d-flex align-center pa-3"
            :class="{ clickable: !!about.originalDeveloper.link }"
          >
            <v-avatar color="primary" size="44" class="me-3">
              <span class="text-white">{{ about.originalDeveloper.initials }}</span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ about.originalDeveloper.name }}</div>
              <div class="text-caption text-medium-emphasis">{{ about.originalDeveloper.role }}</div>
              <div v-if="about.originalDeveloper.link" class="text-caption text-primary mt-1">
                <v-icon size="12">{{ about.originalDeveloper.linkIcon }}</v-icon>
                {{ about.originalDeveloper.linkLabel }}
              </div>
            </div>
          </component>
        </v-col>
      </v-row>
    </v-card>

    <!-- Team -->
    <v-card class="pa-6" elevation="2" rounded="lg">
      <h2 class="text-h6 mb-4 d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-account-group-outline</v-icon>
        Current development team
      </h2>
      <v-row>
        <v-col v-for="person in about.team" :key="person.name" cols="12" sm="6" md="4">
          <component
            :is="person.link ? 'a' : 'div'"
            :href="person.link || undefined"
            :target="person.link ? '_blank' : undefined"
            rel="noopener"
            class="team-card d-flex align-center pa-3"
            :class="{ clickable: !!person.link }"
          >
            <v-avatar color="primary" size="44" class="me-3">
              <span class="text-white">{{ person.initials }}</span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ person.name }}</div>
              <div class="text-caption text-medium-emphasis">{{ person.role }}</div>
              <div v-if="person.link" class="text-caption text-primary mt-1">
                <v-icon size="12">{{ person.linkIcon }}</v-icon>
                {{ person.linkLabel }}
              </div>
            </div>
          </component>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import about from "@/data/about.json";
import logo from "@/assets/G_shaped_logo.png";
import escriptoriumLogo from "@/assets/escriptorium.png";

const integrationLogos = {
  "escriptorium.png": escriptoriumLogo,
};

export default {
  name: "AboutPage",
  data() {
    return {
      websiteName: process.env.VUE_APP_WEBSITE_NAME || "Glyphea",
      about,
      logo,
      features: [
        { text: "Organize projects, documents and pages" },
        { text: "Assign transcribers down to the page level" },
        { text: "Track not transcribed, transcribed and validated lines" },
        { text: "Import from eScriptorium and export to ALTO" },
        { text: "Custom virtual keyboards and glyph fonts for unusual character sets" },
      ],
    };
  },
  methods: {
    integrationLogo(filename) {
      return integrationLogos[filename];
    },
  },
};
</script>

<style scoped>
.about-container {
  padding-top: 32px;
  padding-bottom: 48px;
  max-width: 880px;
}
.hero-logo {
  border-radius: 16px;
}
.feature-list :deep(.v-list-item__prepend) {
  margin-right: 8px;
}
.integration-card,
.team-card {
  border-radius: 12px;
  border: 1px solid rgba(var(--v-border-color), 0.15);
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease, transform 0.15s ease;
}
.integration-card {
  color: inherit;
}
.integration-card:hover {
  background-color: rgba(var(--v-theme-primary), 0.06);
  transform: translateY(-1px);
}
.integration-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.team-card.clickable {
  cursor: pointer;
}
.team-card.clickable:hover {
  background-color: rgba(var(--v-theme-primary), 0.06);
  transform: translateY(-1px);
}
.tech-grid {
  display: flex;
  flex-wrap: wrap;
}
</style>
